import { ImportHistoryApi } from '../../../../api/importHistoryApi';
import { downloadFile, getFileNameFromHeader } from '../../../../api/reportSettingsApi';
import { FileContext } from '../../../../contexts/FileContext';
import { File } from '../../../../interfaces/File';
import { SnackbarParameters } from '../../../../interfaces/Snackbar';
import { Template } from '../../../../interfaces/Templates';
import withSnackbar from '../../../../shared/components/withSnackbar';
import { IMPORT_PROCESS_STATE } from '../../../../shared/constants/ImportPageConstants';
import Summary from '../summary/Summary';
import { SovosDivider } from 'mosaic-react';
import { useContext } from 'react';

interface CalcErrorsImportProps {
	processId: string;
	processStatus: number;
	showSnackbar: (value: SnackbarParameters) => void;
	importTemplate: Template;
	isAudit: boolean;
	file: File;
}

const CalcErrorsImport = (props: CalcErrorsImportProps) => {
	const { processId, processStatus, showSnackbar, importTemplate, isAudit, file } = props;
	const { getFile } = useContext(FileContext);

	const errorFileName = (): string | boolean =>
		processId && processStatus !== IMPORT_PROCESS_STATE.ERROR && processStatus !== IMPORT_PROCESS_STATE.RETRY;

	const handleDownloadOriginalFile = (): void => {
		if (file.id) {
			getFile(file.id)
				.then(response => {
					if (response.status === 200) {
						showSnackbar({ message: 'Download in progress', variant: 'confirmation' });
						downloadFile(file.name, new Blob([response.data]));
					} else if (response.data.errors) {
						showSnackbar({ message: response.data.errors[0].message, variant: 'error' });
					} else {
						showSnackbar({ message: 'Unable to download Original File. Please try again later.', variant: 'error' });
					}
				})
				.catch(error => {
					throw error;
				});
		}
	};

	const handleDownloadErrorsFile = (reportType: string): void => {
		if (processId) {
			ImportHistoryApi.getReportByProcessId(reportType, processId)
				.then(response => {
					if (response.status === 201) {
						showSnackbar({ message: 'Your report is being generated.', variant: 'confirmation' });
					} else if (response.status === 206) {
						showSnackbar({
							message: 'Your report is still being generated. Please wait a few minutes and try again',
							variant: 'warning',
						});
					} else if (response.status === 200) {
						showSnackbar({
							message: 'Report generation was successful, download will start shortly',
							variant: 'confirmation',
						});
						downloadFile(getFileNameFromHeader(response.headers), new Blob([response.data]));
					} else if (response.data.errors) {
						showSnackbar({ message: response.data.errors[0].message, variant: 'error' });
					} else {
						showSnackbar({ message: 'Unable to Export. Please try again later.', variant: 'error' });
					}
				})
				.catch(error => {
					throw error;
				});
		}
	};

	return (
		<div className='content-wrapper'>
			<div style={{ marginBottom: 25 }}>
				{processStatus !== IMPORT_PROCESS_STATE.RETRY ? (
					<>
						{processStatus === IMPORT_PROCESS_STATE.MAX_LIMIT_EXCEEDED ? (
							<div className='process-box process-box--error'>
								<p className='process-box__message'>
									We couldn't process this file because it has over 20,000 lines with the same invoice number. If some
									of the lines represent different transactions, assign them a different invoice number and upload the
									file again. If these lines are from the same transaction, contact Support.
								</p>
							</div>
						) : (
							<div className='process-box process-box--error'>
								<h5 className='process-box__message' style={{ margin: '15px 15px' }}>
									{isAudit
										? 'Your file has been saved with errors.'
										: `We couldn't process every transactions in your file`}
								</h5>
							</div>
						)}
						{processStatus !== IMPORT_PROCESS_STATE.MAX_LIMIT_EXCEEDED && (
							<p style={{ color: '#a0a3af' }}>
								To see what caused the issue, download the error reports, then edit the file and upload it again.
							</p>
						)}
					</>
				) : (
					<>
						<div className='process-box process-box--error'>
							<p className='process-box__message'>Your file failed to save.</p>
						</div>
						<p style={{ color: '#a0a3af' }}>
							Try to save again or check out the Sovos Help Center for more information.
						</p>
					</>
				)}
			</div>

			<div className='info-container'>
				<span>Template: </span>
				<span>{importTemplate.name}</span>
			</div>
			<div className='info-container'>
				<span>Original file: </span>
				<span className='info-container__file' onClick={handleDownloadOriginalFile}>
					{file.name}
				</span>
			</div>
			{errorFileName() && processStatus !== IMPORT_PROCESS_STATE.MAX_LIMIT_EXCEEDED && (
				<div className='info-container' style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline' }}>
					<span>Errors: </span>
					<div>
						<div className='info-container__file' onClick={() => handleDownloadErrorsFile('error')}>
							Download calculation error report
						</div>
						<div className='info-container__file' onClick={() => handleDownloadErrorsFile('validation')}>
							Download configuration error report
						</div>
					</div>
				</div>
			)}
			<SovosDivider style={{ margin: '20px 0px' }} />
			{processStatus !== IMPORT_PROCESS_STATE.MAX_LIMIT_EXCEEDED && (
				<Summary processId={processId} showSnackbar={() => {}} />
			)}
		</div>
	);
};

export default withSnackbar(CalcErrorsImport);
